import { useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { appState } from "../../../state";
import { AvatarImage } from "../../components/AvatarImage";
import type { ChatMessage } from "../../../types";

// Instagram/Twitch-Live-style fading comment stream, not a persistent
// panel - this is the whole point of the redesign: chatting during a
// Together session must never cover the video/photo being watched. Each
// bubble appears, lingers, and fades on its own; pointer-events:none on
// the container so the stream never intercepts taps meant for the
// Lightbox underneath it.
const STREAM_MESSAGE_LIFETIME_MS = 5000;

const fadeInOut = keyframes`
  0% { opacity: 0; transform: translateY(6px); }
  10% { opacity: 1; transform: translateY(0); }
  78% { opacity: 1; }
  100% { opacity: 0; }
`;

// Avatar always on the left, name+time+bubble stacked in a column to its
// right - same layout for both sides (own messages are NOT mirrored to
// the right), matching the reference exactly rather than this app's own
// chat-thread convention.
const Row = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  max-width: 100%;
  animation: ${fadeInOut} ${STREAM_MESSAGE_LIFETIME_MS}ms ease-in-out forwards;
`;

const SenderAvatar = styled.span`
  position: relative;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  border-radius: 50%;
  overflow: hidden;
  background: var(--pink-500);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.68rem;
  font-weight: 700;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  min-width: 0;
`;

const MetaLine = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const SenderName = styled.span<{ $mine: boolean }>`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${(p) => (p.$mine ? "var(--pink-400)" : "#fff")};
`;

const SenderTime = styled.span`
  font-size: 0.66rem;
  color: rgba(255, 255, 255, 0.5);
`;

const Bubble = styled.div`
  background: rgba(20, 20, 20, 0.6);
  color: #fff;
  padding: 6px 10px;
  border-radius: 14px;
  font-size: 0.82rem;
  max-width: 100%;
  word-break: break-word;
  backdrop-filter: blur(4px);
`;

// Real per-message timestamp (message.createdAt), formatted as a plain
// clock time - a relative "2m ago" would be redundant here since these
// bubbles only ever live 5s (they're always effectively "just now"), and
// this matches the same toLocaleTimeString clock-time convention
// utils.ts's formatWatchTogetherCountdown already uses elsewhere.
function clockTime(iso: string): string {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return "";
  return new Date(ms).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function StreamBubble({ message, partnerAvatarFilename, onExpire }: { message: ChatMessage; partnerAvatarFilename: string | null; onExpire: () => void }) {
  useEffect(() => {
    const timer = setTimeout(onExpire, STREAM_MESSAGE_LIFETIME_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isMine = message.senderEmail === appState.currentUserEmail;
  const avatarFilename = isMine ? appState.currentUserAvatarFilename : partnerAvatarFilename;
  const initial = (message.senderName || "?").trim().charAt(0).toUpperCase();

  return (
    <Row>
      <SenderAvatar aria-hidden="true">
        {avatarFilename ? (
          <AvatarImage
            filename={avatarFilename}
            alt=""
            style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }}
            fallback={initial}
          />
        ) : (
          initial
        )}
      </SenderAvatar>
      <Column>
        <MetaLine>
          <SenderName $mine={isMine}>{isMine ? "You" : message.senderName}</SenderName>
          <SenderTime>{clockTime(message.createdAt)}</SenderTime>
        </MetaLine>
        <Bubble>{message.text}</Bubble>
      </Column>
    </Row>
  );
}

export function TogetherChatStream({
  queue,
  partnerAvatarFilename,
  onExpire,
}: {
  queue: ChatMessage[];
  // Real presence data, threaded from TogetherRoot.tsx (which already
  // resolves it for its own status pill) - never a placeholder/initials
  // fallback pretending to be a real photo when this is null.
  partnerAvatarFilename: string | null;
  onExpire: (id: string) => void;
}) {
  if (!queue.length) return null;

  return (
    <div
      style={{
        position: "fixed",
        left: 16,
        bottom: "calc(136px + env(safe-area-inset-bottom, 0px))",
        maxWidth: "min(78vw, 340px)",
        zIndex: 1400,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        pointerEvents: "none",
      }}
    >
      {queue.map((m) => (
        <StreamBubble key={m.id} message={m} partnerAvatarFilename={partnerAvatarFilename} onExpire={() => onExpire(m.id)} />
      ))}
    </div>
  );
}
